import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Building2 } from 'lucide-react';

import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/tables/data-table';
import { createBookingColumns, BookingWithRelations } from '@/components/tables/booking-columns';
import { BookingService } from '@/services/BookingService';
import { BookingDialog } from './booking-dialog';
import { useToast } from '@/hooks/use-toast';

export default function AdminBookingsPage() {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingBooking, setEditingBooking] = useState<BookingWithRelations | null>(null);
    const [search, setSearch] = useState('');
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['bookings', { search }], // Include filters in key
        queryFn: () => BookingService.getBookings({ search, limit: 100 }),
    });

    const bookings = data?.data?.bookings || [];

    const handleCreate = () => {
        setEditingBooking(null);
        setIsDialogOpen(true);
    };

    const handleEdit = (booking: BookingWithRelations) => {
        setEditingBooking(booking);
        setIsDialogOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (!confirm('Are you sure you want to cancel this booking?')) return;
        try {
            await BookingService.deleteBooking(id);
            toast({ title: 'Booking Cancelled', description: 'The booking status has been set to cancelled.' });
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
        } catch (error: any) {
            toast({ title: 'Error', description: 'Failed to cancel booking', variant: 'destructive' });
        }
    };

    const columns = createBookingColumns({
        onEdit: handleEdit,
        onDelete: handleDelete,
    });

    return (
        <div className="container mx-auto p-6 space-y-6">
            <PageHeader
                title="Booking Management"
                description="Unified commercial planning and intent tracking."
            >
                <Button onClick={handleCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    New Booking
                </Button>
            </PageHeader>

            <div className="bg-white rounded-2xl shadow-lg p-6">
                <DataTable
                    columns={columns}
                    data={bookings}
                    searchKey="name"
                    searchPlaceholder="Search bookings..."
                    isLoading={isLoading}
                />
            </div>

            <BookingDialog
                open={isDialogOpen}
                onOpenChange={(open) => {
                    setIsDialogOpen(open);
                    if (!open) setEditingBooking(null);
                }}
                booking={editingBooking}
            />
        </div>
    );
}
